import {
	Avatar,
	Box,
	Card,
	Container,
	Grid,
	Stack,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
	Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Scrollbar from "../../scrollbar/Scrollbar";
import CircleTableHead from "../../circle-contribution-table/CircleTableHead";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	fetchAddRequest,
} from "../../../redux/actions/circleAction";
import {
	selectCurrentUser,
	selectIsError,
	selectUserAddRequests,
} from "../../../redux/selectors/userSelector";
import {
	selectIsLoading,
} from "../../../redux/selectors/circleSelector";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import NotificationModal from "../notification-modal/NotificationModal";
import { isNewlyAddedTimeZone } from "../../../utility/generalUtils";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import { AddRequestTableRow } from "../../allComponents/AllComponents";

interface AddRequestModalProps {
	open: boolean;
	handleClose: () => void;
	userData: any;
}

const AddRequestModal = (props: AddRequestModalProps) => {
	const isLoading = useSelector(selectIsLoading);

	const dispatch = useDispatch<AppDispatch>();

	const userData = useSelector(selectCurrentUser);

	const userAddRequest = useSelector(selectUserAddRequests);

	const error = useSelector(selectIsError);

	const navigate = useNavigate();

	const [initialLoading, setInitialLoading] = useState(true);

	const [page, setPage] = useState(0);

	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [openNav, setOpenNav] = useState(false);

	const [showNotificationModal, setShowNotificationModal] = useState(false);

	const [notifyCircle, setNotifyCircle] = useState({ name: "", image: "" });

	const [timeOutId, setTimeOutId] = useState<any>(null);

	const [notificationModalContent, setNotificationModalContent] = useState("");

	const [notificationTitle, setNotificationTitle] = useState("");

	const ROW_HEIGHT = 65;
	const HEADER_HEIGHT = 50;
	const MIN_TABLE_HEIGHT = 150;

	useEffect(() => {
		const fetchData = async () => {
			try {
				setInitialLoading(true);
				if (userData?._id) {
					await dispatch(fetchAddRequest(userData._id));
				}
			} catch (error: any) {
			} finally {
				setInitialLoading(false);
			}
		};

		fetchData();
	}, [dispatch, userData?._id]);

	// Process the data - server now returns complete objects with union data
	const processedAddRequests = Array.isArray(userAddRequest) 
		? userAddRequest.filter(request => 
			request && typeof request === "object" && request._id && request.union
		) 
		: [];

	const initialHeight = Math.min(processedAddRequests.length * ROW_HEIGHT, 500);

	const [tableHeight, setTableHeight] = useState(initialHeight);

	// Calculate actual rows in the current page
	const actualRowsCurrentPage = Math.min(
		processedAddRequests.length - page * rowsPerPage,
		rowsPerPage
	);

	// Calculate the new height
	const calculateTableHeight = () => {
		const rowsHeight = actualRowsCurrentPage * ROW_HEIGHT;
		const totalHeight = rowsHeight + HEADER_HEIGHT; // include header height
		return Math.max(totalHeight, MIN_TABLE_HEIGHT); // ensure it's not smaller than MIN_TABLE_HEIGHT
	};

	// Updates table height when page, rowsPerPage, or processedAddRequests changes
	useEffect(() => {
		const newHeight = calculateTableHeight();
		setTableHeight(newHeight);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, rowsPerPage, processedAddRequests]);

	const handleChangePage = (event: any, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: any) => {
		setPage(0);
		setRowsPerPage(parseInt(event.target.value, 10));
	};

	const sortedRequests = processedAddRequests.sort((a: any, b: any) => {
		const dateA = new Date(a.created_At || 0).getTime();
		const dateB = new Date(b.created_At || 0).getTime();
		return dateB - dateA;
	});

	const handleOpenNotificationModal = (
		circleName: string,
		circleImage: string
	) => {
		setNotifyCircle({ name: circleName, image: circleImage });
		setShowNotificationModal(true);

		if (timeOutId) clearTimeout(timeOutId);

		const newTimeOutId = setTimeout(() => {
			setShowNotificationModal(false);
			navigate(`/user/${userData?._id}`);
		}, 15000);

		setTimeOutId(newTimeOutId);
	};

	const handleCloseNotificationModal = () => {
		setShowNotificationModal(false);
		setNotificationTitle("");
		setNotificationModalContent("");
		navigate(`/user/${userData?._id}`);
	};

	const handleAcceptAddRequestSuccess = (
		success: boolean,
		message: string,
		circleImage: string
	) => {
		setNotificationModalContent(message);
		setNotifyCircle((prevState) => ({
			...prevState,
			image: circleImage,
		}));

		setShowNotificationModal(true);

		const newTimeOutId = setTimeout(() => {
			setShowNotificationModal(false);
		}, 5000);

		setTimeOutId(newTimeOutId);
	};

	const handleShowNotificationModal = (title: string, message: string) => {
		setNotificationTitle(title);
		setNotificationModalContent(message);
		setShowNotificationModal(true);
	};

	const handleDenyNotification = (circleName: string, circleImage: string) => {
		const message = `You declined to join ${circleName}. No further action is needed.`;
		setNotifyCircle((prevState) => ({
			...prevState,
			image: circleImage,
		}));
		handleShowNotificationModal("Request Denied", message);
	};

	// Show loading spinner if still loading
	if (initialLoading || isLoading) {
		return (
			<>
				<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
				<Box
					sx={{
						minHeight: 1,
						display: "flex",
						flexDirection: { xs: "column", lg: "row" },
					}}
				>
					<DrawerNavbar
						openNav={openNav}
						onCloseNav={() => setOpenNav(false)}
					/>
					<Container
						sx={{
							display: "flex",
							justifyContent: "center",
							alignContent: "center",
							alignItems: "center",
							maxHeight: "100%",
							mt: 12,
						}}
					>
						<LoadingSpinner
							loading={true}
							size={70}
							sx={{ position: "absolute" }}
						/>
					</Container>
				</Box>
			</>
		);
	}

	// Show empty state if no data
	if (processedAddRequests.length === 0) {
		return (
			<>
				<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
				<Box
					sx={{
						minHeight: 1,
						display: "flex",
						flexDirection: { xs: "column", lg: "row" },
					}}
				>
					<DrawerNavbar
						openNav={openNav}
						onCloseNav={() => setOpenNav(false)}
					/>
					<Container
						sx={{
							display: "flex",
							justifyContent: "center",
							alignContent: "center",
							alignItems: "center",
							maxHeight: "100%",
							mt: 12,
						}}
					>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="center"
							textAlign="center"
							sx={{ mt: 25 }}
						>
							<Typography
								variant="h4"
								sx={{
									fontFamily: "'Montserrat', sans-serif",
									fontSize: "1rem",
									fontWeight: 999,
									textAlign: "center",
								}}
							>
								{error || "You Don't Have Any Add Request So Far."}
							</Typography>
						</Stack>
					</Container>
				</Box>
			</>
		);
	}

	// Table is ready, show table
	return (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}
			>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />
				<Container
					sx={{
						display: "flex",
						justifyContent: "center",
						alignContent: "center",
						alignItems: "center",
						maxHeight: "100%",
						mt: 12,
					}}
				>
					<Card sx={{ width: "100%" }}>
						<Stack
							alignItems="center"
							justifyContent="space-between"
							mt={3}
							mb={3}
						>
							<Typography
								variant="h4"
								sx={{
									fontFamily: "'Montserrat', sans-serif",
									fontSize: "1rem",
									fontWeight: 999,
								}}
							>
								{sortedRequests.length > 0
  									? "Circles that have invited you to join as a member"
									: "Circle that has invited you to join as a member"
								}
							</Typography>
						</Stack>
						<Scrollbar sx={{ height: tableHeight }}>
							<TableContainer sx={{ overflow: "auto" }}>
								<Table sx={{ minWidth: 999 }}>
									<CircleTableHead
										headLabel={[
											{ id: "circle", label: "Circle" },
											{ id: "circle leader", label: "Circle Leader" },
											{ id: "members", label: "Members" },
											{ id: "verified", label: "Verified" },
											{ id: "status", label: "Status" },
											{ id: "accept", label: "Accept" },
											{ id: "deny", label: "Deny" },
										]}
									/>
									<TableBody>
										{sortedRequests
											.slice(
												page * rowsPerPage,
												page * rowsPerPage + rowsPerPage
											)
											.map((row: any, i: any) => {

												// Safety check to ensure row has required data
												if (!row || !row._id) {
													return null;
												}

												// Extract data directly from the server response
												const circleDetail = row.union;
												const leaderName = circleDetail?.leader
													? `${circleDetail.leader.firstName || ""} ${circleDetail.leader.lastName || ""}`.trim()
													: "Unknown Leader";
												const circleName = circleDetail?.circleName || "Unknown Circle";
												const circleImage = circleDetail?.circleImage || "";
												const memberCount = circleDetail?.members?.length || 0;
												const isVerified = circleDetail?.isVerified || false;

												return (
													<AddRequestTableRow
														key={row._id || i}
														avatarUrl={circleImage}
														circleName={circleName}
														circleId={circleDetail?._id}
														circleLeaderName={leaderName}
														memberCount={memberCount}
														isVerified={isVerified}
														status={row?.status}
														requestId={row._id}
														handleOpenNotificationModal={() =>
															handleOpenNotificationModal(
																circleName,
																circleImage
															)
														}
														isNew={isNewlyAddedTimeZone(row?.updated_At)}
														onActionComplete={(success, message) =>
															handleAcceptAddRequestSuccess(
																success,
																message,
																circleImage
															)
														}
														onDenyAddRequest={() =>
															handleDenyNotification(
																circleName,
																circleImage
															)
														}
														onSuccess={() =>
															handleShowNotificationModal(
																"Request Accepted",
																`Congratulations! You have successfully joined ${circleName}.`
															)
														}
														onError={() =>
															handleShowNotificationModal(
																"Request Denied",
																"An unexpected error occurred. Please try again."
															)
														}
													/>
												);
											})
											.filter(Boolean)}
									</TableBody>
								</Table>
							</TableContainer>
						</Scrollbar>
						<TablePagination
							page={page}
							component="div"
							count={processedAddRequests.length}
							rowsPerPage={rowsPerPage}
							onPageChange={handleChangePage}
							rowsPerPageOptions={[5, 10, 25]}
							onRowsPerPageChange={handleChangeRowsPerPage}
						/>
					</Card>
				</Container>
			</Box>

			{showNotificationModal && (
				<NotificationModal
					open={showNotificationModal}
					title={notificationTitle}
					handleClose={handleCloseNotificationModal}
					sx={{
						width: "auto",
						maxWidth: "100%",
						margin: "0",
					}}
				>
					<Grid
						container
						spacing={2}
						direction="row"
						alignItems="center"
						justifyContent="center"
						sx={{
							margin: "0",
							width: "100%",
							padding: "0",
						}}
					>
						<Grid item sx={{ maxWidth: "100%", flexBasis: "auto" }}>
							<Avatar
								src={notifyCircle.image}
								sx={{ width: 60, height: 60 }}
								alt="Congrats Icon"
							/>
						</Grid>
						<Grid item xs={9}>
							<Typography
								variant="body2"
								sx={{
									fontSize: 20,
									color: "text.secondary",
									overflow: "hidden",
									overflowWrap: "break-word",
									maxWidth: "100%",
								}}
							>
								{notificationModalContent}
							</Typography>
						</Grid>
					</Grid>
				</NotificationModal>
			)}
		</>
	);
};

export default AddRequestModal;

AddRequestModal.propType = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	userData: PropTypes.object,
};
